import { VehicleSize } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export interface MultiSelectVehicleSizeProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
}

const MultiSelectVehicleSize = ({ isOpen, size = 'md', selectedOptions = [], onChange }: MultiSelectVehicleSizeProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [filter, setFilter] = useState(selectedOptions || []);

  const getVehicleSizeLabel = (size: VehicleSize) => {
    switch (size) {
      case VehicleSize.SMALL:
        return t('vehicle_size.small');
      case VehicleSize.MEDIUM:
        return t('vehicle_size.medium');
      case VehicleSize.BIG:
        return t('vehicle_size.large');
      default:
        return t('vehicle_size.unknown');
    }
  };

  const vehicleSizes = Object.values(VehicleSize);

  // filter search
  const filterOptions = vehicleSizes.map((type: VehicleSize) => ({
    key: type,
    label: getVehicleSizeLabel(type),
  }));

  // check if all selected types are in filter
  const hasSameElements = selectedOptions?.length === filter?.length && selectedOptions.every((value) => filter.find((d) => d === value));

  const onClick = useCallback(() => {
    setFilter(selectedOptions || []);
  }, [selectedOptions]);

  const onChangeSelect = (type: string) => {
    const isSelected = filter.find((d) => d === type);
    const updatedSelected = isSelected ? filter.filter((d) => d !== type) : [...filter, type];
    setFilter(updatedSelected as VehicleSize[]);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setFilter([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(filter);
    handleClose();
  }, [filter, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      strategy='fixed'
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={filter?.length === 0} onClick={() => setFilter([])}>
              {t('vehicle_size.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={filterOptions || []}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item.key} key={item.key} checked={filter.some((d) => d === item.key)} onCheckedChange={() => onChangeSelect(item.key)} />
                  <Label htmlFor={item.key}>{item.label}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('vehicle_size.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('vehicle_size.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='layers-3' />}>
        {t('vehicle_size.title')} {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectVehicleSize;
